import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import {
  firestore, collection, doc, addDoc, updateDoc, getDoc, getDocs,
  deleteDoc, query, orderBy, Timestamp, storage
} from "../firebase-config";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import Layout from "../layouts/Layout";
import "../styles/SermonEditor.css";

// ─── Sermon Form ─────────────────────────────────────────────────────────────

const SermonForm = ({ sermon, onSaved, onCancel }) => {
  const [title, setTitle] = useState(sermon?.title || "");
  const [speaker, setSpeaker] = useState(sermon?.speaker || "");
  const [date, setDate] = useState(
    sermon?.date
      ? (sermon.date.toDate ? sermon.date.toDate() : new Date(sermon.date))
          .toISOString()
          .split("T")[0]
      : ""
  );
  const [series, setSeries] = useState(sermon?.series || "");
  const [scripture, setScripture] = useState(sermon?.scripture || "");
  const [youtubeUrl, setYoutubeUrl] = useState(sermon?.youtubeUrl || "");
  const [audioFileUrl, setAudioFileUrl] = useState(sermon?.audioFileUrl || "");
  const [videoFileUrl, setVideoFileUrl] = useState(sermon?.videoFileUrl || "");
  const [thumbnailUrl, setThumbnailUrl] = useState(sermon?.thumbnailUrl || "");
  const [description, setDescription] = useState(sermon?.description || "");
  const [uploadProgress, setUploadProgress] = useState(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(null);
  const [thumbUploadProgress, setThumbUploadProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/
    );
    return match ? match[1] : null;
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `sermon-audio/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) =>
        setUploadProgress(
          Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
        ),
      (err) => {
        console.error(err);
        setUploadProgress(null);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setAudioFileUrl(url);
        setUploadProgress(null);
      }
    );
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `sermon-videos/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) =>
        setVideoUploadProgress(
          Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
        ),
      (err) => {
        console.error(err);
        setVideoUploadProgress(null);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setVideoFileUrl(url);
        setVideoUploadProgress(null);
      }
    );
  };

  const handleThumbnailUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `sermon-thumbnails/${Date.now()}-${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      (snap) =>
        setThumbUploadProgress(
          Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
        ),
      (err) => {
        console.error(err);
        setThumbUploadProgress(null);
      },
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        setThumbnailUrl(url);
        setThumbUploadProgress(null);
      }
    );
  };

  const handleSave = async () => {
    if (!title.trim() || !speaker.trim() || !date) {
      setError("Please fill in title, speaker, and date.");
      return;
    }
    if (!youtubeUrl && !audioFileUrl && !videoFileUrl) {
      setError("Please provide a YouTube URL, or upload an audio/video file.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const sermonData = {
        title: title.trim(),
        speaker: speaker.trim(),
        date: Timestamp.fromDate(new Date(date)),
        series: series.trim(),
        scripture: scripture.trim(),
        youtubeUrl: youtubeUrl.trim(),
        audioFileUrl,
        videoFileUrl,
        thumbnailUrl,
        description: description.trim(),
        updatedAt: Timestamp.now(),
      };

      if (sermon?.id) {
        await updateDoc(doc(firestore, "sermons", sermon.id), sermonData);
      } else {
        await addDoc(collection(firestore, "sermons"), {
          ...sermonData,
          createdAt: Timestamp.now(),
        });
      }
      onSaved();
    } catch (err) {
      console.error(err);
      setError("Failed to save sermon. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sermon-form">
      <div className="form-group">
        <label htmlFor="sf-title">Sermon Title *</label>
        <input
          id="sf-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. The Grace of God"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sf-speaker">Speaker *</label>
          <input
            id="sf-speaker"
            type="text"
            value={speaker}
            onChange={(e) => setSpeaker(e.target.value)}
            placeholder="e.g. Pastor David"
          />
        </div>
        <div className="form-group">
          <label htmlFor="sf-date">Date *</label>
          <input
            id="sf-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sf-series">Series (optional)</label>
          <input
            id="sf-series"
            type="text"
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            placeholder="e.g. Acts of the Apostles"
          />
        </div>
        <div className="form-group">
          <label htmlFor="sf-scripture">Scripture (optional)</label>
          <input
            id="sf-scripture"
            type="text"
            value={scripture}
            onChange={(e) => setScripture(e.target.value)}
            placeholder="e.g. Romans 8:28-30"
          />
        </div>
      </div>

      {/* YouTube URL */}
      <div className="form-group">
        <label htmlFor="sf-youtube">YouTube URL</label>
        <input
          id="sf-youtube"
          type="url"
          value={youtubeUrl}
          onChange={(e) => setYoutubeUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
        />
        {getYouTubeId(youtubeUrl) && (
          <div className="youtube-preview">
            <iframe
              src={`https://www.youtube.com/embed/${getYouTubeId(youtubeUrl)}`}
              title="YouTube preview"
              allowFullScreen
            />
          </div>
        )}
      </div>

      {/* Audio file upload */}
      <div className="form-group">
        <label>Audio File (optional)</label>
        <div className="file-upload-field">
          {audioFileUrl && (
            <div className="audio-preview">
              <audio controls src={audioFileUrl}>
                Your browser does not support the audio element.
              </audio>
              <button
                type="button"
                className="remove-btn"
                onClick={() => setAudioFileUrl("")}
              >
                Remove
              </button>
            </div>
          )}
          <button
            type="button"
            className="upload-btn"
            onClick={() => audioInputRef.current?.click()}
          >
            {audioFileUrl ? "Replace audio file" : "Upload audio file"}
          </button>
          {uploadProgress !== null && (
            <span className="upload-progress">Uploading… {uploadProgress}%</span>
          )}
          <input
            ref={audioInputRef}
            type="file"
            accept="audio/*"
            style={{ display: "none" }}
            onChange={handleAudioUpload}
          />
        </div>
      </div>

      {/* Video file upload */}
      <div className="form-group">
        <label>Video File (optional)</label>
        <div className="file-upload-field">
          {videoFileUrl && (
            <div className="video-preview">
              <video controls src={videoFileUrl} width="320">
                Your browser does not support the video element.
              </video>
              <button
                type="button"
                className="remove-btn"
                onClick={() => setVideoFileUrl("")}
              >
                Remove
              </button>
            </div>
          )}
          <button
            type="button"
            className="upload-btn"
            onClick={() => videoInputRef.current?.click()}
          >
            {videoFileUrl ? "Replace video file" : "Upload video file"}
          </button>
          {videoUploadProgress !== null && (
            <span className="upload-progress">Uploading… {videoUploadProgress}%</span>
          )}
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            style={{ display: "none" }}
            onChange={handleVideoUpload}
          />
          <p className="help-text">
            Upload a video file as an alternative to YouTube. Supports MP4, WebM, etc.
          </p>
        </div>
      </div>

      {/* Thumbnail */}
      <div className="form-group">
        <label>Thumbnail Image (optional)</label>
        <div className="file-upload-field">
          {thumbnailUrl && (
            <div className="thumb-preview">
              <img src={thumbnailUrl} alt="Sermon thumbnail" />
              <button
                type="button"
                className="remove-btn"
                onClick={() => setThumbnailUrl("")}
              >
                Remove
              </button>
            </div>
          )}
          <button
            type="button"
            className="upload-btn"
            onClick={() => thumbInputRef.current?.click()}
          >
            {thumbnailUrl ? "Change thumbnail" : "Upload thumbnail"}
          </button>
          {thumbUploadProgress !== null && (
            <span className="upload-progress">Uploading… {thumbUploadProgress}%</span>
          )}
          <input
            ref={thumbInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleThumbnailUpload}
          />
          <p className="help-text">
            If not provided, the YouTube thumbnail will be used automatically.
          </p>
        </div>
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="sf-description">Description / Notes (optional)</label>
        <textarea
          id="sf-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional notes or summary for this sermon..."
          rows={4}
        />
      </div>

      {error && <p className="error-message">{error}</p>}

      <div className="button-group">
        <button onClick={handleSave} disabled={saving} className="save-btn">
          {saving ? "Saving…" : sermon?.id ? "Update Sermon" : "Add Sermon"}
        </button>
        <button onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Scripture Parser ─────────────────────────────────────────────────────────

const BIBLE_BOOKS = [
  "Genesis", "Exodus", "Leviticus", "Numbers", "Deuteronomy",
  "Joshua", "Judges", "Ruth", "1 Samuel", "2 Samuel",
  "1 Kings", "2 Kings", "1 Chronicles", "2 Chronicles",
  "Ezra", "Nehemiah", "Esther", "Job", "Psalms", "Psalm",
  "Proverbs", "Ecclesiastes", "Song of Solomon", "Song of Songs",
  "Isaiah", "Jeremiah", "Lamentations", "Ezekiel", "Daniel",
  "Hosea", "Joel", "Amos", "Obadiah", "Jonah", "Micah",
  "Nahum", "Habakkuk", "Zephaniah", "Haggai", "Zechariah", "Malachi",
  "Matthew", "Mark", "Luke", "John", "Acts",
  "Romans", "1 Corinthians", "2 Corinthians", "Galatians", "Ephesians",
  "Philippians", "Colossians", "1 Thessalonians", "2 Thessalonians",
  "1 Timothy", "2 Timothy", "Titus", "Philemon",
  "Hebrews", "James", "1 Peter", "2 Peter",
  "1 John", "2 John", "3 John", "Jude", "Revelation",
  // Common abbreviations
  "Gen", "Ex", "Lev", "Num", "Deut", "Josh", "Judg", "1 Sam", "2 Sam",
  "1 Kgs", "2 Kgs", "1 Chr", "2 Chr", "Neh", "Est", "Ps", "Prov",
  "Eccl", "Isa", "Jer", "Lam", "Ezek", "Dan", "Hos", "Ob", "Mic",
  "Nah", "Hab", "Zeph", "Hag", "Zech", "Mal",
  "Matt", "Mk", "Lk", "Jn", "Rom", "1 Cor", "2 Cor", "Gal", "Eph",
  "Phil", "Col", "1 Thess", "2 Thess", "1 Tim", "2 Tim", "Tit", "Phlm",
  "Heb", "Jas", "1 Pet", "2 Pet", "1 Jn", "2 Jn", "3 Jn", "Rev"
];

const parseScriptureFromText = (text) => {
  if (!text) return "";
  // Sort by length descending so "1 Corinthians" matches before "1 Cor"
  const sortedBooks = [...BIBLE_BOOKS].sort((a, b) => b.length - a.length);
  const booksPattern = sortedBooks.map(b => b.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const regex = new RegExp(
    `(${booksPattern})\\s*(\\d{1,3})(?:\\s*[:.:]\\s*(\\d{1,3})(?:\\s*[-–—]\\s*(\\d{1,3}))?)?`,
    'gi'
  );
  const matches = text.match(regex);
  if (matches && matches.length > 0) {
    return matches[0].trim();
  }
  return "";
};

// ─── YouTube Playlist Import ─────────────────────────────────────────────────

const PlaylistImporter = ({ existingSermons, onImportComplete }) => {
  const [playlistUrl, setPlaylistUrl] = useState("");
  const [speaker, setSpeaker] = useState("Unknown");
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [previewVideos, setPreviewVideos] = useState(null);
  const [playlistTitle, setPlaylistTitle] = useState("");

  const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;

  const getPlaylistId = (url) => {
    if (!url) return null;
    // Handle full URL
    const match = url.match(/[?&]list=([^&]+)/);
    if (match) return match[1];
    // Handle bare ID
    if (url.match(/^PL[A-Za-z0-9_-]+$/)) return url;
    return null;
  };

  const fetchPlaylistVideos = async () => {
    const playlistId = getPlaylistId(playlistUrl);
    if (!playlistId) {
      setImportStatus({ type: "error", message: "Invalid playlist URL or ID." });
      return;
    }
    if (!API_KEY) {
      setImportStatus({ type: "error", message: "YouTube API key not configured. Add REACT_APP_YOUTUBE_API_KEY to your .env file." });
      return;
    }

    setImporting(true);
    setImportStatus(null);

    try {
      // Fetch playlist details for the title (series name)
      const playlistRes = await fetch(
        `https://www.googleapis.com/youtube/v3/playlists?part=snippet&id=${playlistId}&key=${API_KEY}`
      );
      const playlistData = await playlistRes.json();
      const plTitle = playlistData.items?.[0]?.snippet?.title || "";
      setPlaylistTitle(plTitle);

      // Fetch all videos in the playlist (paginated)
      let allVideos = [];
      let nextPageToken = "";

      do {
        const res = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet,contentDetails&maxResults=50&playlistId=${playlistId}&pageToken=${nextPageToken}&key=${API_KEY}`
        );
        const data = await res.json();

        if (data.error) {
          throw new Error(data.error.message);
        }

        allVideos = [...allVideos, ...(data.items || [])];
        nextPageToken = data.nextPageToken || "";
      } while (nextPageToken);

      // Fetch full video descriptions (playlistItems only gives truncated snippets)
      const videoIds = allVideos.map(v => v.contentDetails.videoId).filter(Boolean);
      let fullVideos = [];

      // Batch in groups of 50
      for (let i = 0; i < videoIds.length; i += 50) {
        const batch = videoIds.slice(i, i + 50);
        const vidRes = await fetch(
          `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${batch.join(",")}&key=${API_KEY}`
        );
        const vidData = await vidRes.json();
        fullVideos = [...fullVideos, ...(vidData.items || [])];
      }

      // Check which ones are already imported
      const existingUrls = new Set(
        existingSermons
          .map(s => s.youtubeUrl)
          .filter(Boolean)
          .map(url => {
            const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
            return match ? match[1] : url;
          })
      );

      const newVideos = fullVideos.filter(v => !existingUrls.has(v.id));

      setPreviewVideos(newVideos.map(v => ({
        videoId: v.id,
        title: v.snippet.title,
        description: v.snippet.description,
        publishedAt: v.snippet.publishedAt,
        scripture: parseScriptureFromText(v.snippet.description),
      })));

      setImportStatus({
        type: "info",
        message: `Found ${fullVideos.length} videos in playlist. ${newVideos.length} are new (${fullVideos.length - newVideos.length} already imported).`
      });

    } catch (err) {
      console.error("Error fetching playlist:", err);
      setImportStatus({ type: "error", message: `Failed to fetch playlist: ${err.message}` });
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    if (!previewVideos || previewVideos.length === 0) return;

    setImporting(true);
    setImportStatus({ type: "info", message: "Importing sermons..." });

    let imported = 0;
    let failed = 0;

    for (const video of previewVideos) {
      try {
        const sermonData = {
          title: video.title,
          speaker: speaker.trim() || "Unknown",
          date: Timestamp.fromDate(new Date(video.publishedAt)),
          series: playlistTitle,
          scripture: video.scripture,
          youtubeUrl: `https://www.youtube.com/watch?v=${video.videoId}`,
          audioFileUrl: "",
          videoFileUrl: "",
          thumbnailUrl: "",
          description: video.description,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        await addDoc(collection(firestore, "sermons"), sermonData);
        imported++;
      } catch (err) {
        console.error("Error importing video:", video.title, err);
        failed++;
      }
    }

    setImportStatus({
      type: "success",
      message: `Imported ${imported} sermons${failed > 0 ? ` (${failed} failed)` : ""}. Series: "${playlistTitle}"`
    });
    setPreviewVideos(null);
    setImporting(false);
    onImportComplete();
  };

  return (
    <div className="playlist-importer">
      <h3>Import from YouTube Playlist</h3>
      <p className="help-text">
        Paste a playlist URL or ID. The playlist title becomes the series name,
        and scripture references are auto-detected from video descriptions.
      </p>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="pi-url">Playlist URL or ID</label>
          <input
            id="pi-url"
            type="text"
            value={playlistUrl}
            onChange={(e) => setPlaylistUrl(e.target.value)}
            placeholder="https://www.youtube.com/playlist?list=PL..."
          />
        </div>
        <div className="form-group">
          <label htmlFor="pi-speaker">Speaker for this playlist</label>
          <input
            id="pi-speaker"
            type="text"
            value={speaker}
            onChange={(e) => setSpeaker(e.target.value)}
            placeholder="e.g. Pastor David"
          />
        </div>
      </div>

      <div className="button-group">
        <button
          onClick={fetchPlaylistVideos}
          disabled={importing || !playlistUrl.trim()}
          className="save-btn"
        >
          {importing ? "Fetching…" : "Preview Playlist"}
        </button>
      </div>

      {importStatus && (
        <p className={`import-status ${importStatus.type}`}>
          {importStatus.message}
        </p>
      )}

      {previewVideos && previewVideos.length > 0 && (
        <div className="import-preview">
          <h4>Ready to import {previewVideos.length} sermons:</h4>
          <div className="preview-list">
            {previewVideos.map((video) => (
              <div key={video.videoId} className="preview-item">
                <span className="preview-title">{video.title}</span>
                <span className="preview-meta">
                  {new Date(video.publishedAt).toLocaleDateString("en-GB")}
                  {video.scripture && <> • {video.scripture}</>}
                </span>
              </div>
            ))}
          </div>
          <div className="button-group">
            <button onClick={handleImport} disabled={importing} className="save-btn">
              {importing ? "Importing…" : `Import ${previewVideos.length} Sermons`}
            </button>
            <button onClick={() => setPreviewVideos(null)} className="cancel-btn">
              Cancel
            </button>
          </div>
        </div>
      )}

      {previewVideos && previewVideos.length === 0 && (
        <p className="help-text">All videos from this playlist are already imported.</p>
      )}
    </div>
  );
};

// ─── Sermon Editor Page (Admin) ──────────────────────────────────────────────

const SermonEditor = () => {
  const { loggedInUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [sermons, setSermons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSermon, setEditingSermon] = useState(null);

  useEffect(() => {
    if (!loggedInUser || loggedInUser.role !== "Admin") {
      navigate("/");
      return;
    }
    fetchSermons();
  }, [loggedInUser, navigate]);

  const fetchSermons = async () => {
    setLoading(true);
    try {
      const sermonsQuery = query(
        collection(firestore, "sermons"),
        orderBy("date", "desc")
      );
      const snapshot = await getDocs(sermonsQuery);
      setSermons(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error fetching sermons:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (sermonId) => {
    if (!window.confirm("Are you sure you want to delete this sermon?")) return;
    try {
      await deleteDoc(doc(firestore, "sermons", sermonId));
      setSermons((prev) => prev.filter((s) => s.id !== sermonId));
    } catch (err) {
      console.error("Error deleting sermon:", err);
      alert("Failed to delete sermon.");
    }
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditingSermon(null);
    fetchSermons();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (!loggedInUser || loggedInUser.role !== "Admin") return null;

  return (
    <Layout featureType="online_sermons">
      <div className="sermon-editor-page">
        <div className="sermon-editor-header">
          <h2>Manage Sermons</h2>
          <button onClick={() => navigate("/admin-dashboard")} className="back-btn">
            ← Back to Dashboard
          </button>
        </div>

        {!showForm && !editingSermon && (
          <div className="sermon-editor-actions">
            <button
              className="add-sermon-btn"
              onClick={() => {
                setShowForm(true);
                setEditingSermon(null);
              }}
            >
              + Add New Sermon
            </button>
          </div>
        )}

        {!showForm && !editingSermon && (
          <div className="form-wrapper">
            <PlaylistImporter
              existingSermons={sermons}
              onImportComplete={fetchSermons}
            />
          </div>
        )}

        {showForm && (
          <div className="form-wrapper">
            <h3>New Sermon</h3>
            <SermonForm onSaved={handleSaved} onCancel={() => setShowForm(false)} />
          </div>
        )}

        {editingSermon && (
          <div className="form-wrapper">
            <h3>Edit: {editingSermon.title}</h3>
            <SermonForm
              sermon={editingSermon}
              onSaved={handleSaved}
              onCancel={() => setEditingSermon(null)}
            />
          </div>
        )}

        <div className="sermons-list-admin">
          <h3>All Sermons ({sermons.length})</h3>
          {loading ? (
            <p>Loading sermons…</p>
          ) : sermons.length > 0 ? (
            <div className="sermon-items">
              {sermons.map((sermon) => (
                <div key={sermon.id} className="sermon-item">
                  <div className="sermon-item-info">
                    <h4>{sermon.title}</h4>
                    <p className="sermon-item-meta">
                      {sermon.speaker} • {formatDate(sermon.date)}
                      {sermon.series && <> • {sermon.series}</>}
                    </p>
                    <p className="sermon-item-links">
                      {sermon.youtubeUrl && <span className="badge youtube">YouTube</span>}
                      {sermon.videoFileUrl && <span className="badge video">Video</span>}
                      {sermon.audioFileUrl && <span className="badge audio">Audio</span>}
                    </p>
                  </div>
                  <div className="sermon-item-actions">
                    <button
                      className="edit-btn"
                      onClick={() => {
                        setEditingSermon(sermon);
                        setShowForm(false);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="delete-btn"
                      onClick={() => handleDelete(sermon.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No sermons added yet.</p>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SermonEditor;
